import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  text: string
  variant?: Variant
}

export const AnimatedLabel = memo(function AnimatedLabel({
  text,
  variant = 'default',
}: Props) {
  const [ref, isVisible] = useInView({
    threshold: 0.25,
    triggerOnce: true,
  })

  return (
    <Container
      className={isVisible ? 'visible' : undefined}
      dial={4}
      ref={ref}
      row
      variant={variant}
    >
      {text}
    </Container>
  )
})

const Container = styled(FlexBox)<ContainerProps>`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  line-height: 1.125rem;
  opacity: 0;
  margin-bottom: 1.875rem;
  text-transform: uppercase;
  transform: translateY(3.75rem);
  &.visible {
    opacity: 1;
    transform: none;
    transition: 1.3s ease-in-out;
    &:after {
      width: 7.5rem;
      transition: 0.4s 1s ease-in-out;
    }
  }
  &:after {
    content: '';
    width: 0;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.primaryDark};
    margin-left: 1.25rem;
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'light':
        return css`
          color: ${theme.colors.variants.neutralLight5};
          &:after {
            background: ${theme.colors.variants.neutralLight5};
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    margin-bottom: 1.25rem;
    &.visible {
      &:after {
        width: 3.75rem;
      }
    }
    &:after {
      margin-left: 1rem;
    }
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'light'
